/**
 * Product Availability Manager
 * Dynamic product availability system based on Shopify metafields
 * Replaces static "In stock" text with dynamic availability messages
 */

(function() {
  'use strict';

  window.DEBUG_MODE && console.log('📦 Product Availability Manager: Initializing...');

  // Configuration
  const CONFIG = {
    // Metafield keys
    metafields: {
      availabilityStatus: 'availability_status',
      leadTimeDays: 'lead_time_days', 
      dispatchDateOverride: 'dispatch_date_override',
      customAvailabilityMessage: 'custom_availability_message',
      isBackorderAllowed: 'is_backorder_allowed',
      incomingPoEta: 'incoming_po_eta'
    },
    
    // Availability status values
    statusValues: {
      IN_STOCK: 'in_stock',
      AVAILABLE: 'available',
      PRE_ORDER: 'pre_order',
      OUT_OF_STOCK: 'out_of_stock',
      BACKORDER: 'backorder',
      SPECIAL_ORDER: 'special_order'
    },
    
    // Day-based shipping messages
    shippingMessages: {
      monday: "Order by 3pm for same-day dispatch. Next-day Delivery available.",
      tuesday: "Order by 3pm for same-day dispatch. Next-day Delivery available.",
      wednesday: "Order by 3pm for same-day dispatch. Next-day Delivery available.",
      thursday: "Order by 3pm for same-day dispatch. Next-day Delivery available.",
      friday: "Order by 3pm for same-day dispatch. Next-day Delivery available",
      saturday: "Order by 12pm for same-day dispatch. Sunday Delivery available.",
      sunday: "Order by 3pm on Monday for same-day dispatch. Tuesday Delivery available."
    }
  };

  /**
   * Initialize availability manager for all products on the page
   */
  function init() {
    window.DEBUG_MODE && console.log('📦 Product Availability Manager: Starting initialization...');
    
    // Find all inventory elements that need to be updated
    const inventoryElements = document.querySelectorAll('.product-form__inventory, .inventory, [data-inventory]');
    window.DEBUG_MODE && console.log('📦 Found inventory elements:', inventoryElements.length);
    
    inventoryElements.forEach((element, index) => {
      window.DEBUG_MODE && console.log(`📦 Processing inventory element ${index + 1}:`, element);
      updateInventoryElement(element);
    });
    
    // Also check for product data in JSON scripts
    updateFromProductData();
    
    // Move availability and shipping messages to correct position (with delay to ensure all elements are ready)
    setTimeout(() => {
      moveAvailabilityToCorrectPosition();
    }, 100);
    
    window.DEBUG_MODE && console.log('📦 Product Availability Manager: Initialization complete!');
  }

  /**
   * Update a single inventory element
   */
  function updateInventoryElement(element) {
    // Try to get product data from various sources
    const productData = getProductData();
    
    if (!productData) {
      window.DEBUG_MODE && console.log('📦 No product data found for element:', element);
      return;
    }
    
    window.DEBUG_MODE && console.log('📦 Product data found:', productData);
    
    // Get metafield values
    const metafields = extractMetafields(productData);
    window.DEBUG_MODE && console.log('📦 Extracted metafields:', metafields);
    
    // Generate availability message
    const availabilityMessage = generateAvailabilityMessage(metafields);
    window.DEBUG_MODE && console.log('📦 Generated availability message:', availabilityMessage);
    
    // Update the element
    updateElementContent(element, availabilityMessage, metafields);
  }

  /**
   * Get product data from various sources
   */
  function getProductData() {
    window.DEBUG_MODE && console.log('📦 Getting product data...');
    
    // Method 1: From JSON script tag
    const productJsonScript = document.querySelector('script[data-product-json]');
    window.DEBUG_MODE && console.log('📦 Looking for JSON script tag:', productJsonScript);
    
    if (productJsonScript) {
      try {
        const productData = JSON.parse(productJsonScript.textContent);
        window.DEBUG_MODE && console.log('📦 Product data from JSON script:', productData);
        window.DEBUG_MODE && console.log('📦 Product data keys:', Object.keys(productData));
        
        if (productData.metafields) {
          window.DEBUG_MODE && console.log('📦 Metafields found in JSON:', productData.metafields);
          window.DEBUG_MODE && console.log('📦 Number of metafields:', productData.metafields.length);
        } else {
          window.DEBUG_MODE && console.log('📦 No metafields in JSON data');
        }
        
        return productData;
      } catch (error) {
        console.error('📦 Error parsing product JSON:', error);
        window.DEBUG_MODE && console.log('📦 Raw JSON content:', productJsonScript.textContent.substring(0, 500));
      }
    } else {
      window.DEBUG_MODE && console.log('📦 No JSON script tag found');
    }
    
    // Method 2: From window object (if set by theme)
    if (window.productData) {
      window.DEBUG_MODE && console.log('📦 Product data from window object:', window.productData);
      return window.productData;
    } else {
      window.DEBUG_MODE && console.log('📦 No window.productData found');
    }
    
    // Method 3: From data attributes
    const productContainer = document.querySelector('[data-product-id], .product, .product-form');
    window.DEBUG_MODE && console.log('📦 Looking for product container:', productContainer);
    
    if (productContainer) {
      const productId = productContainer.getAttribute('data-product-id');
      if (productId) {
        window.DEBUG_MODE && console.log('📦 Found product ID:', productId);
        return { id: productId };
      }
    }
    
    window.DEBUG_MODE && console.log('📦 No product data found from any method');
    return null;
  }

  /**
   * Check if the current product is actually out of stock in Shopify
   */
  function checkIfProductIsOutOfStock() {
    window.DEBUG_MODE && console.log('📦 🔍 Checking if product is out of stock in Shopify...');
    
    // Method 1: Check from product data JSON
    const productJsonScript = document.querySelector('script[data-product-json]');
    if (productJsonScript) {
      try {
        const productData = JSON.parse(productJsonScript.textContent);
        window.DEBUG_MODE && console.log('📦 Product data for stock check:', productData);
        
        if (productData.product && productData.product.variants) {
          // Check if any variant is available
          const hasAvailableVariant = productData.product.variants.some(variant => variant.available === true);
          window.DEBUG_MODE && console.log('📦 Has available variant:', hasAvailableVariant);
          
          if (!hasAvailableVariant) {
            window.DEBUG_MODE && console.log('📦 ❌ No variants are available - product is out of stock');
            return true;
          }
        }
        
        // Check selected variant specifically
        if (productData.selected_variant_id) {
          const selectedVariant = productData.product.variants.find(v => v.id === productData.selected_variant_id);
          if (selectedVariant && !selectedVariant.available) {
            window.DEBUG_MODE && console.log('📦 ❌ Selected variant is not available - product is out of stock');
            return true;
          }
        }
      } catch (error) {
        console.error('📦 Error parsing product JSON for stock check:', error);
      }
    }
    
    // Method 2: Check from inventory elements on the page
    const inventoryElements = document.querySelectorAll('.inventory, .product-form__inventory');
    for (let element of inventoryElements) {
      const text = element.textContent.toLowerCase().trim();
      window.DEBUG_MODE && console.log('📦 Checking inventory element text:', text);
      
      if (text.includes('sold out') || text.includes('out of stock') || text.includes('unavailable')) {
        window.DEBUG_MODE && console.log('📦 ❌ Found out of stock text in inventory element');
        return true;
      }
    }
    
    // Method 3: Check if buy buttons are disabled (common pattern for out of stock)
    const buyButtons = document.querySelectorAll('button[type="submit"], .product-form__cart-button, .btn--add-to-cart');
    for (let button of buyButtons) {
      if (button.disabled && (button.textContent.toLowerCase().includes('sold out') || button.textContent.toLowerCase().includes('unavailable'))) {
        window.DEBUG_MODE && console.log('📦 ❌ Found disabled buy button indicating out of stock');
        return true;
      }
    }
    
    window.DEBUG_MODE && console.log('📦 ✅ Product appears to be in stock');
    return false;
  }

  /**
   * Extract metafields from product data
   */
  function extractMetafields(productData) {
    window.DEBUG_MODE && console.log('📦 Extracting metafields from:', productData);
    const metafields = {};
    
    // Method 1: Check if product has metafields array in the new format
    if (productData.metafields && Array.isArray(productData.metafields)) {
      window.DEBUG_MODE && console.log('📦 Processing metafields array (new format):', productData.metafields);
      
      productData.metafields.forEach(metafield => {
        window.DEBUG_MODE && console.log('📦 Processing metafield:', metafield);
        if (metafield.namespace === 'custom') {
          const key = metafield.key;
          let value = metafield.value;
          
          // Handle rich text fields
          if (metafield.type === 'rich_text_field' && value) {
            try {
              const parsed = JSON.parse(value);
              value = parsed?.children?.[0]?.children?.[0]?.value || value;
            } catch (err) {
              // Use raw value if parsing fails
            }
          }
          
          metafields[key] = value;
          window.DEBUG_MODE && console.log(`📦 Found metafield (new format): ${key} = ${value}`);
        }
      });
    }
    
    // Method 2: Check if product has metafields array in old format
    if (productData.product && productData.product.metafields) {
      window.DEBUG_MODE && console.log('📦 Processing metafields from product (old format):', productData.product.metafields);
      
      productData.product.metafields.forEach(metafield => {
        window.DEBUG_MODE && console.log('📦 Processing old format metafield:', metafield);
        if (metafield.namespace === 'custom') {
          const key = metafield.key;
          let value = metafield.value;
          
          // Handle rich text fields
          if (metafield.type === 'rich_text_field' && value) {
            try {
              const parsed = JSON.parse(value);
              value = parsed?.children?.[0]?.children?.[0]?.value || value;
            } catch (err) {
              // Use raw value if parsing fails
            }
          }
          
          metafields[key] = metafield.value;
          window.DEBUG_MODE && console.log(`📦 Found metafield (old format): ${key} = ${value}`);
        }
      });
    }
    
    // Method 3: Check for metafields in data attributes
    const metafieldElements = document.querySelectorAll('[data-metafield-custom]');
    window.DEBUG_MODE && console.log('📦 Found metafield elements:', metafieldElements.length);
    
    metafieldElements.forEach((element, index) => {
      const key = element.getAttribute('data-metafield-custom');
      const value = element.textContent || element.getAttribute('data-metafield-value');
      window.DEBUG_MODE && console.log(`📦 Element ${index}: ${key} = ${value}`);
      if (key && value) {
        metafields[key] = value;
        window.DEBUG_MODE && console.log(`📦 Found metafield from element: ${key} = ${value}`);
      }
    });
    
    window.DEBUG_MODE && console.log('📦 Final extracted metafields:', metafields);
    window.DEBUG_MODE && console.log('📦 Number of metafields found:', Object.keys(metafields).length);
    
    return metafields;
  }

  /**
   * Generate availability message based on metafields
   */
  function generateAvailabilityMessage(metafields) {
    window.DEBUG_MODE && console.log('📦 ===== GENERATING AVAILABILITY MESSAGE =====');
    window.DEBUG_MODE && console.log('📦 All metafields received:', metafields);
    window.DEBUG_MODE && console.log('📦 Looking for availability_status key:', CONFIG.metafields.availabilityStatus);
    window.DEBUG_MODE && console.log('📦 Looking for custom_availability_message key:', CONFIG.metafields.customAvailabilityMessage);
    
    // FIRST: Check if product is actually out of stock in Shopify
    const isProductOutOfStock = checkIfProductIsOutOfStock();
    window.DEBUG_MODE && console.log('📦 🔍 Product out of stock check:', isProductOutOfStock);
    
    if (isProductOutOfStock) {
      window.DEBUG_MODE && console.log('📦 ❌ Product is out of stock in Shopify - showing out of stock message');
      return {
        message: "Currently unavailable – restock date unknown",
        status: 'out_of_stock',
        isAvailable: false,
        hasMetafieldData: true
      };
    }
    
    // Check for custom message override first (only if product is in stock)
    if (metafields[CONFIG.metafields.customAvailabilityMessage]) {
      window.DEBUG_MODE && console.log('📦 ✅ CUSTOM MESSAGE OVERRIDE FOUND:', metafields[CONFIG.metafields.customAvailabilityMessage]);
      const customMessage = metafields[CONFIG.metafields.customAvailabilityMessage];
      // Handle if it's a string or object
      const messageText = typeof customMessage === 'string' ? customMessage : customMessage.value || String(customMessage);
      window.DEBUG_MODE && console.log('📦 Custom message text:', messageText);
      return {
        message: messageText,
        status: 'custom',
        isAvailable: true,
        hasMetafieldData: true
      };
    }
    
    const status = metafields[CONFIG.metafields.availabilityStatus];
    window.DEBUG_MODE && console.log('📦 🔍 Availability status value:', status);
    window.DEBUG_MODE && console.log('📦 🔍 Status type:', typeof status);
    window.DEBUG_MODE && console.log('📦 🔍 Status length:', status ? status.length : 'null/undefined');
    window.DEBUG_MODE && console.log('📦 Available status values:', CONFIG.statusValues);
    
    if (!status || status === '' || status === 'undefined' || status === 'null') {
      window.DEBUG_MODE && console.log('📦 ❌ No valid availability_status metafield found, falling back to default');
      window.DEBUG_MODE && console.log('📦 This will trigger: "In stock" + shipping message');
      return generateDefaultMessage(metafields);
    }
    
    window.DEBUG_MODE && console.log('📦 ✅ Valid status found:', status);
    
    switch (status) {
      case CONFIG.statusValues.IN_STOCK:
        window.DEBUG_MODE && console.log('📦 🎯 Status is IN_STOCK, generating in stock message');
        return generateInStockMessage(metafields);
        
      case CONFIG.statusValues.AVAILABLE:
        window.DEBUG_MODE && console.log('📦 🎯 Status is AVAILABLE, generating available message');
        return generateAvailableMessage(metafields);
        
      case CONFIG.statusValues.PRE_ORDER:
        window.DEBUG_MODE && console.log('📦 🎯 Status is PRE_ORDER, generating pre order message');
        return generatePreOrderMessage(metafields);
        
      case CONFIG.statusValues.OUT_OF_STOCK:
        window.DEBUG_MODE && console.log('📦 🎯 Status is OUT_OF_STOCK, generating out of stock message');
        return generateOutOfStockMessage(metafields);
        
      case CONFIG.statusValues.BACKORDER:
        window.DEBUG_MODE && console.log('📦 🎯 Status is BACKORDER, generating backorder message');
        return generateBackorderMessage(metafields);
        
      case CONFIG.statusValues.SPECIAL_ORDER:
        window.DEBUG_MODE && console.log('📦 🎯 Status is SPECIAL_ORDER, generating special order message');
        return generateSpecialOrderMessage(metafields);
        
      default:
        window.DEBUG_MODE && console.log('📦 ❓ Unknown status:', status, ', falling back to default');
        return generateDefaultMessage(metafields);
    }
  }

  /**
   * Generate in stock message
   */
  function generateInStockMessage(metafields) {
    window.DEBUG_MODE && console.log('📦 ===== GENERATING IN STOCK MESSAGE =====');
    window.DEBUG_MODE && console.log('📦 Metafields for in stock:', metafields);
    
    const leadTime = metafields[CONFIG.metafields.leadTimeDays];
    window.DEBUG_MODE && console.log('📦 Lead time value:', leadTime);
    window.DEBUG_MODE && console.log('📦 Lead time type:', typeof leadTime);
    window.DEBUG_MODE && console.log('📦 Lead time parsed:', parseInt(leadTime));
    
    if (leadTime && parseInt(leadTime) > 0) {
      const days = parseInt(leadTime);
      window.DEBUG_MODE && console.log('📦 ✅ Lead time found:', days, 'days');
      
      if (days === 1) {
        window.DEBUG_MODE && console.log('📦 Result: "In stock – dispatched within 1 working day"');
        return {
          message: "In stock – dispatched within 1 working day",
          status: 'in_stock',
          isAvailable: true,
          hasMetafieldData: true
        };
      } else {
        window.DEBUG_MODE && console.log('📦 Result: "In stock – dispatched within', days, 'working days"');
        return {
          message: `In stock – dispatched within ${days} working days`,
          status: 'in_stock',
          isAvailable: true,
          hasMetafieldData: true
        };
      }
    }
    
    // Metafield status is set but no lead time - show default metafield behavior
    window.DEBUG_MODE && console.log('📦 ❌ No lead time found, using default metafield behavior');
    window.DEBUG_MODE && console.log('📦 Result: "In stock – dispatched within 1 working day"');
    return {
      message: "In stock – dispatched within 1 working day",
      status: 'in_stock',
      isAvailable: true,
      hasMetafieldData: true
    };
  }

  /**
   * Generate available message (for items that need more processing time)
   */
  function generateAvailableMessage(metafields) {
    const leadTime = metafields[CONFIG.metafields.leadTimeDays];
    
    if (leadTime && parseInt(leadTime) > 0) {
      const days = parseInt(leadTime);
      return {
        message: `Available – dispatches in approx. ${days} working days`,
        status: 'available',
        isAvailable: true,
        hasMetafieldData: true
      };
    }
    
    // Default to 3 days if no lead time specified
    return {
      message: "Available – dispatches in approx. 3 working days",
      status: 'available',
      isAvailable: true,
      hasMetafieldData: true
    };
  }

  /**
   * Generate pre-order message
   */
  function generatePreOrderMessage(metafields) {
    const dispatchDate = metafields[CONFIG.metafields.dispatchDateOverride];
    window.DEBUG_MODE && console.log('📦 Pre-order dispatch date raw:', dispatchDate);
    window.DEBUG_MODE && console.log('📦 Pre-order dispatch date type:', typeof dispatchDate);
    
    if (dispatchDate && dispatchDate.trim() !== '') {
      const date = new Date(dispatchDate);
      window.DEBUG_MODE && console.log('📦 Parsed date:', date);
      
      if (!isNaN(date.getTime())) {
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        window.DEBUG_MODE && console.log('📦 Formatted date:', formattedDate);
        return {
          message: `Pre-order now – estimated dispatch: ${formattedDate}`,
          status: 'pre_order',
          isAvailable: true,
          hasMetafieldData: true
        };
      } else {
        window.DEBUG_MODE && console.log('📦 Invalid date format, falling back to lead time');
      }
    }
    
    const leadTime = metafields[CONFIG.metafields.leadTimeDays];
    if (leadTime && parseInt(leadTime) > 0) {
      const days = parseInt(leadTime);
      // Calculate estimated dispatch date based on lead time
      const estimatedDate = new Date();
      estimatedDate.setDate(estimatedDate.getDate() + days);
      const formattedDate = estimatedDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      return {
        message: `Pre-order now – estimated dispatch: ${formattedDate}`,
        status: 'pre_order',
        isAvailable: true,
        hasMetafieldData: true
      };
    }
    
    return {
      message: "Pre-order now – estimated dispatch: TBC",
      status: 'pre_order',
      isAvailable: true,
      hasMetafieldData: true
    };
  }

  /**
   * Generate out of stock message
   */
  function generateOutOfStockMessage(metafields) {
    const isBackorderAllowed = metafields[CONFIG.metafields.isBackorderAllowed];
    const incomingEta = metafields[CONFIG.metafields.incomingPoEta];
    
    // Check if backorders are allowed - if yes, show as backorder instead
    if (isBackorderAllowed === 'true' || isBackorderAllowed === true) {
      window.DEBUG_MODE && console.log('📦 Backorder is allowed, redirecting to backorder message');
      return generateBackorderMessage(metafields);
    }
    
    if (incomingEta && incomingEta.trim() !== '') {
      const date = new Date(incomingEta);
      
      if (!isNaN(date.getTime())) {
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        return {
          message: `Currently unavailable – expected restock: ${formattedDate}`,
          status: 'out_of_stock',
          isAvailable: false,
          hasMetafieldData: true
        };
      }
    }
    
    return {
      message: "Currently unavailable – restock date unknown",
      status: 'out_of_stock',
      isAvailable: false,
      hasMetafieldData: true
    };
  }

  /**
   * Generate backorder message
   */
  function generateBackorderMessage(metafields) {
    // Priority 1: Check for incoming PO ETA (when new stock arrives)
    const incomingEta = metafields[CONFIG.metafields.incomingPoEta];
    
    if (incomingEta && incomingEta.trim() !== '') {
      const date = new Date(incomingEta);
      
      if (!isNaN(date.getTime())) {
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        return {
          message: `Backorder – estimated dispatch: ${formattedDate}`,
          status: 'backorder',
          isAvailable: true,
          hasMetafieldData: true
        };
      }
    }
    
    // Priority 2: Check for dispatch date override
    const dispatchDate = metafields[CONFIG.metafields.dispatchDateOverride];
    
    if (dispatchDate && dispatchDate.trim() !== '') {
      const date = new Date(dispatchDate);
      
      if (!isNaN(date.getTime())) {
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        return {
          message: `Backorder – estimated dispatch: ${formattedDate}`,
          status: 'backorder',
          isAvailable: true,
          hasMetafieldData: true
        };
      }
    }
    
    // Priority 3: Use lead time to calculate dispatch date
    const leadTime = metafields[CONFIG.metafields.leadTimeDays];
    if (leadTime && parseInt(leadTime) > 0) {
      const days = parseInt(leadTime);
      return {
        message: `Backorder – estimated dispatch in ${days} working days`,
        status: 'backorder',
        isAvailable: true,
        hasMetafieldData: true
      };
    }
    
    return {
      message: "Backorder – estimated dispatch: TBC",
      status: 'backorder',
      isAvailable: true,
      hasMetafieldData: true
    };
  }

  /**
   * Generate special order message
   */
  function generateSpecialOrderMessage(metafields) {
    const leadTime = metafields[CONFIG.metafields.leadTimeDays];
    
    if (leadTime && parseInt(leadTime) > 0) {
      const days = parseInt(leadTime);
      return {
        message: `Special order – ships in ${days} working days`,
        status: 'special_order',
        isAvailable: true,
        hasMetafieldData: true
      };
    }
    
    return {
      message: "Special order – ships in 5–7 working days",
      status: 'special_order',
      isAvailable: true,
      hasMetafieldData: true
    };
  }

  /**
   * Generate default message (fallback)
   */
  function generateDefaultMessage(metafields) {
    window.DEBUG_MODE && console.log('📦 ===== GENERATING DEFAULT MESSAGE =====');
    window.DEBUG_MODE && console.log('📦 This means NO availability_status metafield was found');
    window.DEBUG_MODE && console.log('📦 Metafields received:', metafields);
    
    // First check if product is actually out of stock
    const isProductOutOfStock = checkIfProductIsOutOfStock();
    if (isProductOutOfStock) {
      window.DEBUG_MODE && console.log('📦 ❌ Product is out of stock - showing out of stock message');
      return {
        message: "Currently unavailable – restock date unknown",
        status: 'out_of_stock',
        isAvailable: false,
        hasMetafieldData: false
      };
    }
    
    // Check if we have any inventory data from the original element
    const originalElement = document.querySelector('.inventory--high, .inventory--low, .inventory');
    window.DEBUG_MODE && console.log('📦 Original element found:', originalElement);
    
    if (originalElement) {
      const originalText = originalElement.textContent.trim();
      window.DEBUG_MODE && console.log('📦 Original text:', originalText);
      
      if (originalText.includes('stock')) {
        window.DEBUG_MODE && console.log('📦 ✅ Using original element text:', originalText);
        return {
          message: originalText,
          status: 'default',
          isAvailable: true,
          hasMetafieldData: false
        };
      }
    }
    
    // No metafield data - show simple "In stock" (shipping message will be added dynamically)
    window.DEBUG_MODE && console.log('📦 ✅ Using default "In stock" message');
    window.DEBUG_MODE && console.log('📦 Result: "In stock" (shipping message will be added dynamically)');
    return {
      message: "In stock",
      status: 'default',
      isAvailable: true,
      hasMetafieldData: false
    };
  }

  /**
   * Update element content with new availability message
   */
  function updateElementContent(element, availabilityData, metafields) {
    window.DEBUG_MODE && console.log('📦 ===== UPDATING ELEMENT CONTENT =====');
    window.DEBUG_MODE && console.log('📦 Availability data:', availabilityData);
    window.DEBUG_MODE && console.log('📦 Element to update:', element);
    
    // Clear existing content
    element.innerHTML = '';
    
    // Create structured message HTML with separate spans
    let messageHTML;
    
    if (availabilityData.status === 'custom') {
      window.DEBUG_MODE && console.log('📦 🎯 Creating custom message HTML');
      // For custom messages, keep as single span
      messageHTML = `<span class="availability-message availability-${availabilityData.status}">${availabilityData.message}</span><span class="availability_tip_icon"><img src="https://cdn.shopify.com/s/files/1/0359/1356/4293/files/Frame_2087328116.png?v=1760550719"></span>`;
    } else if (!availabilityData.hasMetafieldData) {
      window.DEBUG_MODE && console.log('📦 🎯 Creating message with shipping (NO metafield data)');
      // For any status without metafield data, combine with shipping message
      const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      const dayKey = currentDay === 'mon' ? 'monday' :
                     currentDay === 'tue' ? 'tuesday' :
                     currentDay === 'wed' ? 'wednesday' :
                     currentDay === 'thu' ? 'thursday' :
                     currentDay === 'fri' ? 'friday' :
                     currentDay === 'sat' ? 'saturday' : 'sunday';
      
      const shippingMessage = CONFIG.shippingMessages[dayKey];
      window.DEBUG_MODE && console.log('📦 Current day:', currentDay, '->', dayKey);
      window.DEBUG_MODE && console.log('📦 Shipping message:', shippingMessage);
      
      messageHTML = `<span class="availability-message availability-${availabilityData.status}">
        <span class="availability-status-text">${availabilityData.message}</span> 
        <span class="shipping-message-text">${shippingMessage}</span>
     <span class="availability_tip_icon">   <img src="https://cdn.shopify.com/s/files/1/0359/1356/4293/files/Frame_2087328116.png?v=1760550719"></span>
      </span>`;
    } else {
      window.DEBUG_MODE && console.log('📦 🎯 Creating structured message with metafield data');
      window.DEBUG_MODE && console.log('📦 Has metafield data:', availabilityData.hasMetafieldData);
      // For structured messages with metafield data, split into separate spans
      const messageParts = availabilityData.message.split(' – ');
      window.DEBUG_MODE && console.log('📦 Message parts:', messageParts);
      
      if (messageParts.length === 2) {
        // Split into status and details
        const statusPart = messageParts[0].trim();
        const detailsPart = messageParts[1].trim();
        window.DEBUG_MODE && console.log('📦 Status part:', statusPart);
        window.DEBUG_MODE && console.log('📦 Details part:', detailsPart);
        
        messageHTML = `<span class="availability-message availability-${availabilityData.status}">
          <span class="availability-status-text">${statusPart}</span>
          <span class="availability-details-text">${detailsPart}</span>
          <span class="availability_tip_icon"><img src="https://cdn.shopify.com/s/files/1/0359/1356/4293/files/Frame_2087328116.png?v=1760550719"></span>
        </span>`;
      } else {
        // Fallback for messages without separator
        window.DEBUG_MODE && console.log('📦 Single part message:', availabilityData.message);
        messageHTML = `<span class="availability-message availability-${availabilityData.status}">
          <span class="availability-status-text">${availabilityData.message}</span>
          <span class="availability_tip_icon"><img src="https://cdn.shopify.com/s/files/1/0359/1356/4293/files/Frame_2087328116.png?v=1760550719"></span>
        </span>`;
      }
    }
    
    window.DEBUG_MODE && console.log('📦 Final HTML:', messageHTML);
    
    // Update the element with structured HTML
    element.innerHTML = messageHTML;
    
    // Update parent classes
    element.className = element.className.replace(/inventory--high|inventory--low|inventory/, '');
    element.classList.add('availability-status', `availability-${availabilityData.status}`);
    
    if (!availabilityData.isAvailable) {
      element.classList.add('availability-unavailable');
    }
    
    window.DEBUG_MODE && console.log('📦 ✅ Element updated successfully!');
  }

  /**
   * Update from product data (alternative method)
   */
  function updateFromProductData() {
    // This method can be used if product data is available globally
    if (window.productData && window.productData.metafields) {
      window.DEBUG_MODE && console.log('📦 Updating from global product data');
      const metafields = {};
      
      window.productData.metafields.forEach(metafield => {
        if (metafield.namespace === 'custom') {
          metafields[metafield.key] = metafield.value;
        }
      });
      
      const availabilityMessage = generateAvailabilityMessage(metafields);
      
      // Update all inventory elements
      const inventoryElements = document.querySelectorAll('.product-form__inventory, .inventory');
      inventoryElements.forEach(element => {
        updateElementContent(element, availabilityMessage, metafields);
      });
    }
  }

  /**
   * Move availability message and shipping message to the correct position after price info
   */
  function moveAvailabilityToCorrectPosition() {
    window.DEBUG_MODE && console.log('📦 Moving availability and shipping messages to correct position...');
    
    // Find the entire availability status span with its wrapper
    let availabilityStatusSpan = document.querySelector('.product-form__inventory.inventory.availability-status');
    if (!availabilityStatusSpan) {
      // Try finding it in the original location
      availabilityStatusSpan = document.querySelector('.availability-status');
    }
    
    if (!availabilityStatusSpan) {
      window.DEBUG_MODE && console.log('📦 No availability status span found to move');
      return;
    }
    
    // Find the shipping message (look in multiple possible locations)
    let shippingMessage = document.querySelector('.shipping-message');
    if (!shippingMessage) {
      window.DEBUG_MODE && console.log('📦 No shipping message found to move');
    }
    
    // Find the target location - look for price info in multiple locations
    let priceInfoItem = document.querySelector('.product-form__info-item .product-form__info-content .price-list');
    if (!priceInfoItem) {
      // Try alternative selectors
      priceInfoItem = document.querySelector('.price-list');
    }
    
    if (!priceInfoItem) {
      window.DEBUG_MODE && console.log('📦 No price info item found');
      return;
    }
    
    let targetParent = priceInfoItem.closest('.product-form__info-item');
    if (!targetParent) {
      // If no parent found, create a container or find another suitable location
      targetParent = document.querySelector('.product-form__info-list');
      if (!targetParent) {
        window.DEBUG_MODE && console.log('📦 No suitable target parent found');
        return;
      }
    }
    
    // Create new availability info item
    const newAvailabilityItem = document.createElement('div');
    newAvailabilityItem.className = 'product-form__info-item';
    
    const newAvailabilityContent = document.createElement('div');
    newAvailabilityContent.className = 'product-form__info-content meta-info';
    
    // Move the entire availability status span
    newAvailabilityContent.appendChild(availabilityStatusSpan);
    newAvailabilityItem.appendChild(newAvailabilityContent);
    
    // Insert availability message after the price info item
    if (targetParent.parentNode) {
      targetParent.parentNode.insertBefore(newAvailabilityItem, targetParent.nextSibling);
    } else {
      targetParent.appendChild(newAvailabilityItem);
    }
    
    // If shipping message exists, create a separate info item for it
    if (shippingMessage) {
      const newShippingItem = document.createElement('div');
      newShippingItem.className = 'product-form__info-item';
      
      const newShippingContent = document.createElement('div');
      newShippingContent.className = 'product-form__info-content meta-info';
      
      // Move the shipping message
      newShippingContent.appendChild(shippingMessage);
      newShippingItem.appendChild(newShippingContent);
      
      // Insert shipping message after the availability message
      newAvailabilityItem.parentNode.insertBefore(newShippingItem, newAvailabilityItem.nextSibling);
      
      window.DEBUG_MODE && console.log('📦 Shipping message moved to correct position');
    }
    
    window.DEBUG_MODE && console.log('📦 Availability status span moved to correct position');
  }

  /**
   * Add styles for availability messages
   */
  function addStyles() {
    if (document.getElementById('availability-manager-styles')) return;

    const styles = `
      <style id="availability-manager-styles">
        .availability-message {
          font-weight: 500;
          font-size: 14px;
          line-height: 1.4;
        }
        
        .availability-in_stock .availability-message {
          color: #10b981;
        }
        
        .availability-available .availability-message {
          color: #14b8a6;
        }
        
        .availability-pre_order .availability-message {
          color: #f59e0b;
        }
        
        .availability-backorder .availability-message {
          color: #8b5cf6;
        }
        
        .availability-special_order .availability-message {
          color: #6366f1;
        }
        
        .availability-out_of_stock .availability-message,
        .availability-unavailable .availability-message {
          color: #ef4444;
        }
        
        .shipping-message-text {
          font-size: 14px;
          color: #374151;
          font-weight: 400;
          margin-left: 8px;
        }
        
        .availability-status {
          transition: all 0.3s ease;
        }
      </style>
    `;

    document.head.insertAdjacentHTML('beforeend', styles);
  }

  // Initialize when DOM is ready
  function start() {
    addStyles();
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  start();

  window.DEBUG_MODE && console.log('📦 Product Availability Manager: Script loaded!');

})();
